import React, {
  useEffect,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { ConstituentType } from '../../constants/MaterialModels';
import {
  ControlLabel,
  FormControl,
  FormGroup,
  InputGroup,
} from 'react-bootstrap';
import {
  Row,
  Col,
} from 'react-bootstrap/lib';
import SideBar from '../SideBar/SideBar';
import { useStores } from '../../hooks/use-stores';
import type { MixDesignItem } from '../../../types/batch/MixDesignItem';
import { SIDEBAR_SCREEN } from '../../stores/sidebar-store';
import { observer } from 'mobx-react';
import {
  RemoteBatchData,
  RemoteControlEventType,
} from '../../containers/Batch/RemoteBatchController';

export const ConfirmFinishBatch = observer(() => {
  const { t } = useTranslation();
  const { batchStore, materialStore, sidebarStore } = useStores();
  const [ waterTarget, setWaterTarget ] = useState<number>(0);
  const [ waterId, setWaterId ] = useState<number>(0);
  const [ unit, setUnit ] = useState<string>('');
  const data = batchStore.running.length ? batchStore.running[0] : null;
  const waters = data?.mixDesign?.filter((c: MixDesignItem) => c.type === ConstituentType.WATER);

  useEffect(() => {
    if (
      (batchStore.isFinishBatchPanelVisible && !sidebarStore.isOpen(SIDEBAR_SCREEN.BATCH_FINISH))
      ||
      (!batchStore.isFinishBatchPanelVisible && sidebarStore.isOpen(SIDEBAR_SCREEN.BATCH_FINISH))
    ) {
      sidebarStore.onToggle(SIDEBAR_SCREEN.BATCH_FINISH);
    }

  }, [sidebarStore, batchStore.isFinishBatchPanelVisible]);

  const onWaterTargetChange = (e: React.ChangeEvent<FormControl & HTMLInputElement>) => {
    const v = Number(e.currentTarget.value);
    setWaterTarget(Number.isNaN(v) ? 0 : v);
  };

  const onWaterSelect = (e: React.ChangeEvent<FormControl & HTMLInputElement>) => {
    const waterId = Number(e.currentTarget.value);
    setWaterId(waterId);
    setUnit(materialStore.getMaterialUnitSymbol(waterId));
  };

  const onWaterAdd = (waterId: number, waterTarget: number) => {
    const runningData = batchStore.running.length ? batchStore.running[0] : null;

    if (batchStore.canSendRemoteInstruction()) {
      batchStore.sendRemoteInstruction(RemoteBatchData(RemoteControlEventType.ADD_EXTRA_WATER, null, {
        id: runningData?.id,
        waterId,
        waterTarget,
      }));
      setWaterTarget(0);
      return;
    }

    batchStore.startExtraWater(runningData, waterId, waterTarget);
    setWaterTarget(0);
  };

  const onFinishBatch = () => {
    const runningData = batchStore.running.length ? batchStore.running[0] : null;

    if (batchStore.canSendRemoteInstruction()) {
      batchStore.sendRemoteInstruction(RemoteBatchData(RemoteControlEventType.FINISH_BATCH, null, {
        id: runningData?.id,
      }));
      return;
    }

    batchStore.finishBatch(runningData, true);
  };

  return (
    <SideBar type={SIDEBAR_SCREEN.BATCH_FINISH} title={t('Finish the Load')} icon="fa fa-stop">
      <div className="confirm-finish-batch">
        <h4>
          {t('The load is completed')}.
        </h4>
        {data && (
          <div>
            <h4>
              {t('Please finish the load or add more water')}.
            </h4>
            <Row>
              <Col xs={4}>
                <FormGroup>
                  <ControlLabel>{t('Constituent')}</ControlLabel>
                  <FormControl
                    name="waterId"
                    componentClass="select"
                    value={waterId}
                    onChange={onWaterSelect}
                    bsSize="sm"
                  >
                    <option value="" />
                    {waters?.map((item: MixDesignItem) => (
                      <option
                        key={item.constituentItemId}
                        value={item.constituentItemId}
                      >
                        {item.constituentItemCode}
                      </option>
                    ))}
                  </FormControl>
                </FormGroup>
              </Col>
              <Col xs={4}>
                <FormGroup>
                  <ControlLabel>{t('Quantity')}</ControlLabel>
                  <InputGroup>
                    <FormControl
                      name="waterTarget"
                      componentClass="input"
                      value={waterTarget || ''}
                      onChange={onWaterTargetChange}
                      bsSize="sm"
                    />
                    <InputGroup.Addon>
                      {unit}
                    </InputGroup.Addon>
                  </InputGroup>
                </FormGroup>
              </Col>
              <Col xs={4}>
                <button
                  className="btn btn-primary margin-top-20"
                  disabled={!waterId || !waterTarget}
                  onClick={() => onWaterAdd(waterId, waterTarget)}
                >
                  {t('Add')}
                </button>
              </Col>
            </Row>
            <Row>
              <Col xs={4}>
                <button
                  className="btn btn-primary margin-top-20"
                  disabled={!data}
                  onClick={() => onFinishBatch()}
                >
                  {t('Finish')}
                </button>
              </Col>
            </Row>
          </div>
        )}
      </div>
    </SideBar>
  );
});
